import React, { useCallback, useMemo, useState } from 'react'
import { connect, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { RouteKey } from '@src/store/constants'

import { selectIsProjectSwitchInitiated } from '@store/actionSlices/appConfig'
import { selectProjectIdentity } from '@store/actionSlices/projectIdentity'
import {
  InteractiveMapItemInterface,
  ProjectIdentity,
  RootStateTypeExtra,
} from '@store/types'

import useProjectSwitchUtils from '@utilities/useProjectSwitchUtils'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import { ProjectType } from '@adUtilities/constants'
import useMapActionRouteUtils from '@adUtilities/map-action-route-utils'
import { notifyError } from '@adUtilities/notifier'
import { DefaultRouteParamKey, HouseAndLandRoute } from '@adUtilities/types'

import InteractiveMapLayer from './interactive-map-layer'

export interface ComponentPropsInterface {
  projectIdentity: ProjectIdentity
  itemData: InteractiveMapItemInterface
  onItemClick: (groupId: string) => void
  isActive: boolean
}

function InteractiveMapItemHnl({
  projectIdentity,
  itemData,
  onItemClick,
  isActive,
}: ComponentPropsInterface) {
  const history = useHistory()
  const { actionRoute } = itemData
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const { type: currentProjectType } = useSelector(selectProjectIdentity)

  const { parseActionRoute } = useMapActionRouteUtils({
    currentProjectType,
  })

  const { isValid, route, routeParams, projectType } = useMemo(
    () => parseActionRoute(actionRoute),
    [actionRoute, parseActionRoute]
  )

  const { startProjectSwitch } = useProjectSwitchUtils()
  const isRefreshing = useSelector(selectIsProjectSwitchInitiated)
  const [isProjectSwitching, setIsProjectSwitching] = useState(false)

  const hasValidProjectSwitchParam = isValid && !!routeParams.project

  const handleClick = useCallback(() => {
    if (isRefreshing) {
      return
    }
    onItemClick(itemData.groupId)
  }, [onItemClick, itemData.groupId, isRefreshing])

  const handleProjectSwitch = useCallback(async () => {
    if (!isValid) {
      return
    }
    const project = routeParams[DefaultRouteParamKey.Project]

    if (!project) {
      return
    }

    setIsProjectSwitching(true)
    const { status, message } = await startProjectSwitch({
      projectCanonicalName: project,
      redirectTo: String(route),
      redirectContext: routeParams,
    })
    setIsProjectSwitching(false)

    if (!status && message) {
      notifyError(message)
    }
  }, [startProjectSwitch, route, routeParams])

  const handleClickLink = React.useCallback<React.MouseEventHandler>(
    async (e) => {
      e.preventDefault()
      e.stopPropagation()

      if (isRefreshing || isProjectSwitching || !isValid) {
        return
      }

      if (hasValidProjectSwitchParam && routeParams.project) {
        handleProjectSwitch()
        return
      }

      if (projectType !== ProjectType.HouseAndLand) {
        return
      }

      await firebaseControlQuery.update({
        [`houseAndLand.activePrecinctId`]: routeParams.precinct || '',
        [`houseAndLand.activeStageId`]: routeParams.stage || '',
        [`houseAndLand.activeLotId`]: routeParams.lot || '',
      })
      if (!route) {
        return
      }
      if (route === HouseAndLandRoute.Stage && routeParams.lot) {
        firebaseControlQuery.updateRoute(route)
        history.push(RouteKey.LOT_VIEW)
        return
      }
      firebaseControlQuery.updateRoute(route)
    },
    [
      handleProjectSwitch,
      projectType,
      route,
      routeParams,
      isValid,
      isRefreshing,
      isProjectSwitching,
      hasValidProjectSwitchParam,
    ]
  )

  return (
    <InteractiveMapLayer
      label={itemData.label}
      route={(isValid && route) || undefined}
      isActive={isActive}
      isValid={isValid}
      onClick={handleClick}
      onClickLink={handleClickLink}
      isProcessing={isProjectSwitching}
      as={hasValidProjectSwitchParam ? 'button' : undefined}
    />
  )
}

export default connect(({ projectIdentity }: RootStateTypeExtra) => ({
  projectIdentity,
}))(InteractiveMapItemHnl)
